import { Fragment, useEffect, useRef, useState } from 'react';
import { Staff } from '../../types/Staff';
import StaffService from '../../services/staff.service';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import Dropdown from '../common/dropdown';
import { useTranslation } from 'react-i18next';
import MultiSelect from '../common/MultiSelect';
import FleetsService from '../../services/fleets.services';
import { DropdownOption } from '../../models/DropdownOption';
import { toastError, toastSuccess } from '../../App';
import DeleteStaffModal from './DeleteStaffModal';

interface Option {
  value: string;
  label: string;
}

interface StaffForm {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  userType: any;
}

export default function StaffModal(props: {
  open: boolean;
  rows: Staff[];
  onSave: () => {};
  setOpen: (open: boolean) => void;
  selectedStaffId?: string | null;
}) {
  const { t } = useTranslation();
  const cancelButtonRef = useRef(null);
  const isUpdate = props.selectedStaffId;
  const [formData, setFormData] = useState<StaffForm>({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    userType: undefined,
  });
  const [initialStaff, setInitialStaff] = useState<Staff | null>(null);
  const [countryCode, setCountryCode] = useState("+1");
  const canSave =
    formData.firstName.trim() !== '' &&
    formData.lastName.trim() !== '' &&
    formData.email.trim() !== '' &&
    formData.phoneNumber.trim() !== '' &&
    formData.phoneNumber.trim().length >= (countryCode === "+1" ? 12 : 13) &&
    formData?.userType?.value !== undefined;
  const [selectedOptions, setSelectedOptions] = useState<Option[]>([]);
  const [fleets, setFleets] = useState<Option[]>([]);
  const [phone, setPhone] = useState("");
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const userTypes: DropdownOption<string>[] = [
    // { value: 'ADMIN', label: t('staff.admin') },
    { value: 'MANAGER', label: t('staff.manager') },
    { value: 'STAFF', label: t('staff.staff') },
  ];

  const handleSave = async () => {
    const isUpdating = isUpdate && props.selectedStaffId;

    const staffMember = isUpdating ? createUpdatedStaffMember() : createNewStaffMember();

    try {
      let response;
      if (isUpdating && props.selectedStaffId) {
        response = await StaffService.updateStaff(props.selectedStaffId, staffMember);
      } else {
        response = await StaffService.addStaff(staffMember);
      }

      if (response.ok) {
        props.onSave();
        toastSuccess(t('staff.saved'));
      } else {
        toastError(t('staff.saveError'));
      }
    } finally {
      props.setOpen(false);
    }
  };

  const createUpdatedStaffMember = () => {
    const { firstName, lastName, email, phoneNumber, userType } = formData;
    return {
      ...(initialStaff?.firstName !== firstName && { firstName }),
      ...(initialStaff?.lastName !== lastName && { lastName }),
      ...(initialStaff?.email !== email && { email }),
      ...(initialStaff?.phoneNumber !== phoneNumber && { phoneNumber }),
      ...(initialStaff?.userType !== userType.value && { userType: userType.value }),
      fleets: selectedOptions.map((option) => option.value),
    };
  };

  const createNewStaffMember = () => ({
    ...formData,
    userType: formData.userType.value,
    fleets: selectedOptions.map((option) => option.value),
  });

  const handleDelete = async () => {
    if (!props.selectedStaffId) return;
    
    try {
      const response = await StaffService.deleteStaff(props.selectedStaffId);
      if (response.ok) {
        props.onSave();
        toastSuccess(t('staff.staffDeleted'));
        props.setOpen(false);
      } else {
        toastError(t('staff.deleteError'));
      }
    } catch (error) {
      console.error('Failed to delete staff:', error);
      toastError(t('staff.deleteError'));
    }
  };

  useEffect(() => {
    if (props.selectedStaffId) {
      StaffService.getStaffById(props.selectedStaffId).then((response) => {
        response.json().then((data) => {
          setInitialStaff(data);
          const countryCode = data.phoneNumber.startsWith("+1") ? "+1" : "+52";
          const phoneNumber = data.phoneNumber.replace(countryCode, "");
          setPhone(formatPhoneNumber(phoneNumber, countryCode));
          setCountryCode(countryCode);

          setFormData({
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
            phoneNumber: data.phoneNumber,
            userType: userTypes.find((option) => option.value === data.userType)
          });

          setSelectedOptions(
            data.fleets.map((fleet: any) => ({
              value: fleet._id,
              label: fleet.name,
            })),
          );
        });
      });
    }
    else {
      setInitialStaff(null);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        userType: null,
      });
      setPhone('');
      setCountryCode('+1');
      setSelectedOptions([]);
    }
  }, [props.selectedStaffId]);

  const fetchFleets = async () => {
    const response = await FleetsService.getFleets();
    const data = await response.json();

    const fleets = data.map((fleet: any) => ({
      value: fleet._id,
      label: fleet.name,
    }));

    setFleets(fleets);
  };

  useEffect(() => {
    fetchFleets();
  }, []);

  const handleChange = (event: any) => {
    setFormData({
      ...formData,
      [event.target.name]: event.target.value,
    });
  };

  const formatPhoneNumber = (phoneNumber: string, countryCode: string) => {
    // Remove non-numeric characters
    let cleaned = phoneNumber.replace(/\D/g, "");

    // Format for USA (+1): (XXX) XXX-XXXX
    if (countryCode === "+1") {
      if (cleaned.length > 6) {
        return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
      } else if (cleaned.length > 3) {
        return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
      }
    }

    // Format for Mexico (+52): XX XXXX XXXX
    if (countryCode === "+52") {
      if (cleaned.length > 6) {
        return `${cleaned.slice(0, 2)} ${cleaned.slice(2, 6)} ${cleaned.slice(6, 10)}`;
      } else if (cleaned.length > 2) {
        return `${cleaned.slice(0, 2)} ${cleaned.slice(2)}`;
      }
    }

    return cleaned;
  };

  const handlePhoneChange = (str: string) => {
    let cleaned = str.replace(/\D/g, "").slice(0, 10);

    setPhone(formatPhoneNumber(str, countryCode));

    setFormData({
      ...formData,
      phoneNumber: countryCode + cleaned,
    });
  };

  const handleCountryChange = (e: any) => {
    setCountryCode(e.target.value);
    setPhone("");
  };

  return (
    <>
      <Transition.Root show={props.open} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-10"
          initialFocus={cancelButtonRef}
          onClose={props.setOpen}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="mt-24 relative transform rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl sm:p-6">
                  <div>
                    <div className="flex justify-between">
                      <h2 className="text-lg font-medium leading-6 text-gray-900">
                        {t('staff.staffTitle')}
                      </h2>

                      <button
                        type="button"
                        className="flex rounded-md p-1 border-transparent bg-red-600 text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                        ref={cancelButtonRef}
                        onClick={() => props.setOpen(false)}
                      >
                        <XMarkIcon
                          className="h-7 w-7"
                          aria-hidden="true"
                        ></XMarkIcon>
                      </button>
                    </div>

                    <div className="mt-6 grid grid-cols-4 gap-6">
                      <div className="col-span-4 sm:col-span-2">
                        <label
                          htmlFor="first-name"
                          className="block text-sm font-medium text-gray-700"
                        >
                          {t('staff.firstName')}
                        </label>
                        <input
                          type="text"
                          name="firstName"
                          id="first-name"
                          autoComplete="off"
                          className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-gray-900 focus:outline-none focus:ring-gray-900 sm:text-sm"
                          value={formData.firstName}
                          onChange={handleChange} />
                      </div>

                      <div className="col-span-4 sm:col-span-2">
                        <label htmlFor="last-name" className="block text-sm font-medium text-gray-700">
                          {t('staff.lastName')}
                        </label>
                        <input
                          type="text"
                          name="lastName"
                          id="last-name"
                          autoComplete="off"
                          className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-gray-900 focus:outline-none focus:ring-gray-900 sm:text-sm"
                          value={formData.lastName}
                          onChange={handleChange} />
                      </div>

                      <div className="col-span-4 sm:col-span-2">
                        <label
                          htmlFor="email"
                          className="block text-sm font-medium text-gray-700"
                        >
                          {t('staff.email')}
                        </label>
                        <input
                          type="text"
                          name="email"
                          id="email"
                          autoComplete="off"
                          className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-gray-900 focus:outline-none focus:ring-gray-900 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                          value={formData.email}
                          onChange={handleChange}
                          disabled={!!isUpdate}
                        />
                      </div>

                      <div className="col-span-4 sm:col-span-2">
                        <label htmlFor="phone-number" className="block text-sm font-medium text-gray-700">
                          {t('staff.phoneNumber')}
                        </label>

                        <div className={`flex flex-row mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus-within:border-gray-900 focus-within:ring-1 focus-within:ring-gray-900`}>
                          <select value={countryCode} onChange={handleCountryChange} className='rounded-md border-none focus:outline-none focus:ring-0 focus:border-transparent'>
                            <option value="+1">🇺🇸 +1</option>
                            <option value="+52">🇲🇽 +52</option>
                          </select>

                          <input
                            type="text"
                            value={phone}
                            max={10}
                            onChange={e => handlePhoneChange(e.target.value)}
                            className="rounded-md border-none py-2 px-3 focus:outline-none focus:ring-0 focus:border-transparent sm:text-sm"
                          />
                        </div>
                      </div>

                      <div className="col-span-4 sm:col-span-2">
                        <div className="col-span-12">
                          <MultiSelect
                            title={t('fleets.title')}
                            options={fleets}
                            selectedOptions={selectedOptions}
                            setSelectedOptions={setSelectedOptions} />
                        </div>
                      </div>

                      {!isUpdate && (
                        <div className="ml-auto col-span-4 sm:col-span-2">
                          <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                            {t('staff.userType')}
                          </label>

                          <Dropdown
                            options={userTypes}
                            selectedOption={formData.userType}
                            onChange={(option) => setFormData({ ...formData, userType: option })} />
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                    {isUpdate && (
                      <button
                        type="button"
                        className="inline-flex w-full justify-center rounded-md border border-transparent bg-red-600 hover:bg-red-700 px-4 py-2 text-base font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:text-sm"
                        onClick={() => setIsDeleteModalOpen(true)}
                      >
                        {t('staff.deleteStaff')}
                      </button>
                    )}
                    <button
                      type="button"
                      className={`inline-flex w-full justify-center rounded-md border border-transparent ${canSave ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-indigo-200'} px-4 py-2 text-base font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:col-start-2 sm:text-sm`}
                      disabled={!canSave}
                      onClick={handleSave}
                    >
                      {t('save')}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      <DeleteStaffModal
        isOpen={isDeleteModalOpen}
        setOpen={setIsDeleteModalOpen}
        onConfirm={handleDelete}
        staffName={initialStaff ? `${initialStaff.firstName} ${initialStaff.lastName}` : undefined}
      />
    </>
  );
}
